<template>
  <v-app>
    <beta-banner @open-feedback="handleOpenFeedback" />
    <app-header v-if="!route.meta.hideHeader" />
    <v-main>
      <router-view></router-view>
    </v-main>
    <app-footer v-if="!route.meta.hideFooter" />
    <feedback-modal ref="feedbackModal" hide-fab />
    <auth-required-modal />
  </v-app>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useTheme } from 'vuetify'
import { useAuthStore } from '@/stores/auth'
import { AUTH_SESSION_INVALIDATED_EVENT } from '@/utils/authSession'
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import FeedbackModal from '@/components/common/FeedbackModal.vue'
import BetaBanner from '@/components/layout/BetaBanner.vue'
import AuthRequiredModal from '@/components/auth/AuthRequiredModal.vue'

const route = useRoute()
const authStore = useAuthStore()
const feedbackModal = ref<InstanceType<typeof FeedbackModal> | null>(null)
const vuetifyTheme = useTheme()

// Keep CSS custom-property class in sync with Vuetify theme at all times.
// This is the single source of truth watcher — ThemeToggle handles user
// interaction, this handles any other programmatic theme changes.
watch(
  () => vuetifyTheme.global.name.value,
  (name) => {
    if (name === 'light') {
      document.documentElement.classList.add('theme-light')
    } else {
      document.documentElement.classList.remove('theme-light')
    }
    localStorage.setItem('theme-preference', name)
  },
  { immediate: false },
)

const handleOpenFeedback = () => {
  feedbackModal.value?.openFeedback()
}

const handleSessionInvalidated = () => {
  authStore.clearSession()
}

onMounted(async () => {
  window.addEventListener(AUTH_SESSION_INVALIDATED_EVENT, handleSessionInvalidated)

  // In development mode, try to auto-login via backend bypass if not already authenticated
  if (import.meta.env.DEV && !authStore.isAuthenticated) {
    console.log('[App] Dev mode detected, attempting auto-login...');
    try {
      await authStore.fetchUser();
      // If successful, we're now authenticated
    } catch (e) {
      console.warn('[App] Dev auto-login failed:', e);
    }
  }

  // Check if session is valid on app load
  // This prevents stale local auth state from rendering signed-in UI while session is expired.
  if (authStore.isAuthenticated) {
    const isSessionValid = await authStore.validateSession()
    if (!isSessionValid) {
      authStore.clearSession()
    }
  }
})

onUnmounted(() => {
  window.removeEventListener(AUTH_SESSION_INVALIDATED_EVENT, handleSessionInvalidated)
})
</script>

<style>
/* Global styles - Let Vuetify and individual components handle backgrounds */
html, body, #app {
  margin: 0;
  padding: 0;
}

/* iOS Safari: Comprehensive overscroll fix */
html {
  background: var(--bg-canvas);
  overflow-x: clip !important; /* Prevent horizontal scroll */
  overflow-y: auto !important;
  overscroll-behavior-x: none;
  overscroll-behavior-y: contain;
  /* Lock width to viewport */
  width: 100vw;
  max-width: 100vw;
  position: relative;
  /* iOS-specific: Disable horizontal touch panning */
  touch-action: pan-y pinch-zoom;
}

body {
  background: var(--bg-canvas);
  overflow-x: clip !important; /* Prevent horizontal scroll */
  overscroll-behavior-x: none;
  overscroll-behavior-y: contain;
  /* Lock width to viewport */
  width: 100%;
  max-width: 100vw;
  position: relative;
  /* iOS-specific: Prevent momentum scrolling artifacts */
  -webkit-overflow-scrolling: touch;
  /* iOS-specific: Disable horizontal touch panning */
  touch-action: pan-y pinch-zoom;
}

.v-application {
  color: var(--text-body);
  background: var(--bg-canvas) !important;
  /* Prevent horizontal overflow */
  overflow-x: clip !important;
  max-width: 100vw;
}

/* iOS Safari: Lock all containers to viewport width */
#app,
.v-application,
.v-application__wrap {
  width: 100%;
  max-width: 100vw;
  overflow-x: clip !important;
  position: relative;
}

/* Fix for sticky positioning: Ensure Vuetify wrappers allow vertical scroll */
.v-main,
.v-main__wrap {
  overflow-x: clip !important;
  overflow-y: visible !important;
  max-width: 100vw;
}

/* iOS Safari: Prevent horizontal overscroll by ensuring body covers full viewport */
body::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bg-canvas);
  z-index: var(--z-behind);
  pointer-events: none;
}
</style>
